import { ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { isFreeTrialPromoCode } from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useIsDeviceWidth, DeviceWidth } from '@/utils/deviceWidthHelpers'
import { fetchUserCatchUpBookkeepingStatus } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import {
  selectUserShouldSeeOnboardingDashboard,
  selectOnboardingStepCompletedOrSkipped,
} from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'

import { FreeTrialGettingStarted } from './free-trial-onboarding/FreeTrialGettingStarted'
import { RecommendedForYou } from './free-trial-onboarding/RecommendedForYou'
import { SupportForYou } from './free-trial-onboarding/SupportForYou'
import { GettingStarted } from './GettingStarted'
import { OnboardingDashboardSurvey } from './OnboardingDashboardSurvey'
import { LearnWithHeard } from './LearnWithHeard'

import { PracticeOverviewCard } from '@/features/user/dashboard/PracticeOverviewCard'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { MaximizeTaxDeductionsCard } from '@/features/user/dashboard/insights-cards/MaximizeTaxDeductionsCard'
import { Card, CardColors } from '@/ui'

// TODO: Migrate to V2 skipcq: JS-0099
import { Loader } from 'semantic-ui-react'

const OnboardingDashboardWrapper = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const isTablet = useIsDeviceWidth(DeviceWidth.tablet)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)

  return (
    <div className={`w-full ${className}`}>
      <Card
        color={CardColors.GREEN}
        className={`
            ${isMobile ? 'mt-4 py-8 px-4' : isTablet ? 'mt-4 py-16 px-6' : 'py-16 px-10'} 
            getting-started
          `}
      >
        {hasFreeTrialPromoCode ? (
          <FreeTrialGettingStarted />
        ) : (
          <GettingStarted />
        )}
      </Card>
      <div
        className={`
            ${isMobile ? 'pt-10 px-4 pb-6' : isTablet ? 'p-10' : 'py-14 px-10'} 
            additional-onboarding-content
          `}
      >
        {children}
      </div>
    </div>
  )
}

export const OnboardingDashboard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const userShouldSeeOnboardingDashboard = useReselector(
    selectUserShouldSeeOnboardingDashboard
  )
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)

  const hasConnectedBankAccount = useReselector(
    selectOnboardingStepCompletedOrSkipped,
    GettingStartedOnboardingStep.connectBusinessBankAccount
  )

  const enableLeverOne = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableLever1EarlyAccessToCentralizedWeeklyDashboardMvp,
    false
  )

  const showObSurvey = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.showOnboardingSurveys,
    false
  )

  const enableTaxDeductionsDashboard = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableTaxDeductionsDashboard,
    false
  )

  useEffect(() => {
    async function batchFetch() {
      await Promise.all([fetchUserCatchUpBookkeepingStatus()(dispatch)])
      setLoading(false)
    }
    batchFetch()
  }, [dispatch])

  useEffect(() => {
    if (!userShouldSeeOnboardingDashboard) {
      navigate('/dashboard')
    }
  }, [navigate, userShouldSeeOnboardingDashboard])

  if (loading) {
    return <Loader active />
  }

  const showPracticeOverview = enableLeverOne && hasConnectedBankAccount

  if (hasFreeTrialPromoCode) {
    return (
      <OnboardingDashboardWrapper>
        <RecommendedForYou />
        <br />
        <br />
        <br />
        <SupportForYou />
      </OnboardingDashboardWrapper>
    )
  } else {
    return (
      <OnboardingDashboardWrapper>
        {showPracticeOverview && (
          <>
            <PracticeOverviewCard />
            {enableTaxDeductionsDashboard && <MaximizeTaxDeductionsCard />}
          </>
        )}
        {!showPracticeOverview && <LearnWithHeard />}
        {showObSurvey && <OnboardingDashboardSurvey />}
      </OnboardingDashboardWrapper>
    )
  }
}
